import { FC, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { makeSxStyles } from "ui";
import useTeamMembers from "../hooks/useTeamMembers";
import useTeamMembersFilters from "../hooks/useTeamMembersFilters";
import useAddMemberForm from "../hooks/useAddMemberForm";
import useMemberAccessToggle from "../hooks/useMemberAccessToggle";
import useDeleteMember from "../hooks/useDeleteMember";
import useResendInviteConfirm from "../hooks/useResendInviteConfirm";
import TeamMembersListHeader from "./TeamMembersListHeader";
import TeamMemberRow from "./TeamMemberRow";
import AddMemberRow from "./AddMemberRow";
import MemberAccessConfirmDialog from "./MemberAccessConfirmDialog";
import MemberDeleteConfirmDialog from "./MemberDeleteConfirmDialog";
import ResendInviteConfirmDialog from "./ResendInviteConfirmDialog";

export interface TeamMembersSectionProps {
  organizationId: string;
  isOrganizationActive: boolean;
}

const TeamMembersSection: FC<TeamMembersSectionProps> = ({
  organizationId,
  isOrganizationActive,
}) => {
  const styles = useStyles();
  const [isAdding, setIsAdding] = useState(false);
  const { filters, updateFilter, sortQueryParams } = useTeamMembersFilters();
  const { members, isLoading } = useTeamMembers(
    organizationId,
    sortQueryParams,
  );
  const { memberForm, isLoading: isInviting } = useAddMemberForm(
    organizationId,
    () => setIsAdding(false),
  );
  const {
    pendingMember: memberPendingAccessToggle,
    isSubmitting: isTogglingAccess,
    requestToggle,
    confirmToggle,
    cancelToggle,
  } = useMemberAccessToggle();
  const {
    pendingMember: memberPendingDelete,
    isSubmitting: isDeleting,
    requestDelete,
    confirmDelete,
    cancelDelete,
  } = useDeleteMember();
  const {
    pendingMember: memberPendingResend,
    isSubmitting: isResending,
    requestResend,
    confirmResend,
    cancelResend,
  } = useResendInviteConfirm();

  const hasMembers = members.length > 0;

  const handleCancelAdd = () => {
    memberForm.resetForm();
    setIsAdding(false);
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>Team Members</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setIsAdding(true)}
          disabled={isAdding}
          data-testid="add-member-button"
        >
          Add Member
        </Button>
      </Box>

      <Box sx={styles.list}>
        {isAdding && (
          <AddMemberRow
            memberForm={memberForm}
            isLoading={isInviting}
            onCancel={handleCancelAdd}
          />
        )}
        {hasMembers && (
          <TeamMembersListHeader
            sortOptions={filters.sortOptions}
            onSortChange={(sortOptions) =>
              updateFilter("sortOptions", sortOptions)
            }
          />
        )}
        {!isLoading &&
          members.map((member) => (
            <TeamMemberRow
              key={member.id}
              member={member}
              isOrganizationActive={isOrganizationActive}
              onAccessToggleRequest={requestToggle}
              onResendInviteRequest={requestResend}
              onDeleteRequest={requestDelete}
            />
          ))}
        {!isLoading && !hasMembers && !isAdding && (
          <Box sx={styles.emptyCard}>
            <Typography sx={styles.emptyText}>No Team Members Added</Typography>
          </Box>
        )}
      </Box>
      <MemberAccessConfirmDialog
        member={memberPendingAccessToggle}
        isSubmitting={isTogglingAccess}
        onConfirm={confirmToggle}
        onCancel={cancelToggle}
      />
      <MemberDeleteConfirmDialog
        member={memberPendingDelete}
        isSubmitting={isDeleting}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
      <ResendInviteConfirmDialog
        member={memberPendingResend}
        isSubmitting={isResending}
        onConfirm={confirmResend}
        onCancel={cancelResend}
      />
    </Box>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  container: {
    mt: "40px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: "16px",
  },
  title: {
    fontWeight: 700,
    fontSize: "24px",
    lineHeight: "32px",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  emptyCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "56px",
    border: `1px solid ${grey[400]}`,
    borderRadius: "12px",
  },
  emptyText: {
    color: grey[100],
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
  },
}));

export default TeamMembersSection;
